import {
  Box,
  Button,
  Drawer,
  Flex,
  HStack,
  IconButton,
  Menu,
  Portal,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  FiCalendar,
  FiChevronDown,
  FiClock,
  FiGrid,
  FiList,
  FiLogOut,
  FiMenu,
  FiSettings,
  FiShield,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { useLogout, useMe } from "@/hooks/useAuth";
import { useSyncAppearance } from "@/hooks/useSyncAppearance";
import { preloadRoute, prefetchRouteData, type RouteName } from "@/lib/preload";
import ChangelogPopover from "./ChangelogPopover";
import DryRunBanner from "./DryRunBanner";
import ImpersonationBanner from "./ImpersonationBanner";
import NotificationBell from "./NotificationBell";
import WorkspaceProjectSwitcher from "./WorkspaceProjectSwitcher";
import ProjectSettingsModal from "./ProjectSettingsModal";

interface NavItem {
  to: string;
  route: RouteName;
  label: string;
  icon: ReactNode;
}

const navItems: NavItem[] = [
  { to: "/dashboard", route: "dashboard", label: "Dashboard", icon: <FiGrid /> },
  { to: "/clients", route: "clients", label: "Clients", icon: <FiUsers /> },
  { to: "/calendar", route: "calendar", label: "Calendar", icon: <FiCalendar /> },
  { to: "/scheduled", route: "scheduled", label: "Scheduled", icon: <FiList /> },
  { to: "/history", route: "history", label: "History", icon: <FiClock /> },
];

export default function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const logout = useLogout();
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false);
  useSyncAppearance();

  // Warm both the route chunk and its first-page data on hover/focus so
  // the click feels instant — the chunk is downloading and the API
  // request is in flight before navigation even happens.
  function warm(route: RouteName) {
    preloadRoute(route);
    prefetchRouteData(route, qc);
  }

  const firstName = me?.displayName?.split(" ")[0] ?? me?.firstName ?? "Me";

  // Hide the per-project nav buttons until the operator has picked a
  // project — otherwise the Clients/Calendar/etc. links would 412 on
  // every API call. They land on the picker (ProjectsPicker page) instead.
  const showProjectNav = Boolean(me?.lastActiveProjectId);

  return (
    <Flex direction="column" h="100dvh">
      {/* Impersonation banner first — when a sysadmin is acting as a
          user, this should be the topmost visual cue, above even the
          dry-run warning. Renders null for regular customers. */}
      <ImpersonationBanner />
      <DryRunBanner />

      <Box
        as="header"
        position="sticky"
        top="0"
        zIndex={10}
        borderBottomWidth="1px"
        bg="bg.panel"
        flexShrink={0}
      >
        <Box maxW="1400px" mx="auto">
        {/* Topbar px matches the page content's px below — so the
            Callwright button's left edge lines up exactly with whatever
            heading the active page renders ("Notifications", "Call
            History", etc.). Was 3 on base which caused a 4px offset. */}
        <HStack
          px={{ base: 4, md: 8, xl: 10 }}
          py={3}
          gap={{ base: 2, md: 6 }}
        >
          {/* Mobile: hamburger opens a drawer that holds BOTH the
              workspace+project picker AND the page nav links — the
              Callwright topbar dropdown is hidden below md because its
              two-line brand+subtitle pushed the bell + profile menu off
              the right edge on narrow viewports.
              Desktop: the Callwright dropdown sits inline in the topbar
              and the hamburger is hidden. */}
          <IconButton
            hideFrom="md"
            variant="ghost"
            size="sm"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
          >
            <FiMenu />
          </IconButton>

          <Box hideBelow="md">
            <WorkspaceProjectSwitcher
              onOpenProjectSettings={() => setProjectSettingsOpen(true)}
            />
          </Box>

          <HStack as="nav" gap={2} hideBelow="md" ml={4} display={showProjectNav ? undefined : "none"}>
            {navItems.map((item) => {
              const active = pathname.startsWith(item.to);
              return (
                <Button
                  key={item.to}
                  asChild
                  size="sm"
                  variant={active ? "subtle" : "ghost"}
                  colorPalette={active ? "blue" : "gray"}
                >
                  <Link
                    to={item.to}
                    onMouseEnter={() => warm(item.route)}
                    onFocus={() => warm(item.route)}
                    onTouchStart={() => warm(item.route)}
                  >
                    <HStack gap={2}>
                      {item.icon}
                      <Text>{item.label}</Text>
                    </HStack>
                  </Link>
                </Button>
              );
            })}
          </HStack>

          <Spacer />

          <NotificationBell />

          {me ? (
            <Menu.Root
              positioning={{ placement: "bottom-end" }}
              onSelect={(d) => {
                if (d.value === "profile") navigate("/profile");
                else if (d.value === "settings") navigate("/settings");
                else if (d.value === "admin") navigate("/admin");
                else if (d.value === "logout") logout.mutate();
              }}
            >
              <Menu.Trigger asChild>
                <Button variant="ghost" size="sm">
                  <HStack gap={1}>
                    <Text hideBelow="sm" maxW="12rem" truncate>
                      {me.displayName || me.email}
                    </Text>
                    <Text hideFrom="sm">{firstName}</Text>
                    <FiChevronDown />
                  </HStack>
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content minW="12rem">
                    <Menu.ItemGroup>
                      <Menu.ItemGroupLabel>
                        {me.displayName || me.email}
                      </Menu.ItemGroupLabel>
                      <Menu.Item value="profile">
                        <FiUser />
                        <Box flex="1">Profile</Box>
                      </Menu.Item>
                      <Menu.Item value="settings">
                        <FiSettings />
                        <Box flex="1">Settings</Box>
                      </Menu.Item>
                    </Menu.ItemGroup>
                    {/* Phase A — Admin panel link, only rendered for
                        users with a SystemAdmin row. Visually distinct
                        (red icon) so it's hard to miss / confuse with a
                        customer menu item. */}
                    {me.isSystemAdmin ? (
                      <>
                        <Menu.Separator />
                        <Menu.Item value="admin">
                          <Box color="red.500" display="inline-flex">
                            <FiShield />
                          </Box>
                          <Box flex="1" color="red.500" fontWeight="medium">
                            Admin panel
                          </Box>
                        </Menu.Item>
                      </>
                    ) : null}
                    <Menu.Separator />
                    <Menu.Item value="logout" color="red.500">
                      <FiLogOut />
                      <Box flex="1">Sign out</Box>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          ) : null}
        </HStack>
        </Box>
      </Box>

      <Drawer.Root
        open={drawerOpen}
        onOpenChange={(d) => setDrawerOpen(d.open)}
        placement="start"
      >
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content maxW="18rem">
              {/* Same Callwright dropdown as the desktop topbar — tapping
                  it opens the workspace + project picker (with active-row
                  green check and gear-for-settings). Reusing the component
                  means mobile and desktop stay visually + behaviourally in
                  lock-step; no separate inline list to keep aligned. */}
              <Drawer.Header borderBottomWidth="1px" px={3} py={2}>
                <WorkspaceProjectSwitcher
                  onOpenProjectSettings={() => {
                    setProjectSettingsOpen(true);
                    setDrawerOpen(false);
                  }}
                />
              </Drawer.Header>
              <Drawer.Body p={3}>
                {showProjectNav ? (
                  <Stack gap={1}>
                    {navItems.map((item) => {
                      const active = pathname.startsWith(item.to);
                      return (
                        <Button
                          key={item.to}
                          asChild
                          justifyContent="flex-start"
                          variant={active ? "subtle" : "ghost"}
                          colorPalette={active ? "blue" : "gray"}
                          size="md"
                        >
                          <Link
                            to={item.to}
                            onClick={() => setDrawerOpen(false)}
                            onTouchStart={() => warm(item.route)}
                          >
                            <HStack gap={3}>
                              {item.icon}
                              <Text>{item.label}</Text>
                            </HStack>
                          </Link>
                        </Button>
                      );
                    })}
                  </Stack>
                ) : (
                  <Text color="fg.muted" fontSize="sm" p={2}>
                    Pick a project to access these tools.
                  </Text>
                )}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      <ProjectSettingsModal
        open={projectSettingsOpen}
        onOpenChange={setProjectSettingsOpen}
      />

      {/* Major-release "What's new" popover. Self-gated: renders null
          unless there's an unread major changelog AND this session
          hasn't shown it yet. */}
      <ChangelogPopover />

      {/* Main content area is a non-scrolling flex column that fills
          whatever vertical space the topbar (+ optional dry-run banner)
          leaves behind. Each page picks its own scroll strategy:
          long-table pages (Clients / History / Scheduled) keep their
          heading + filters + pagination fixed and scroll only the
          table body; short pages set overflowY="auto" at their root
          and scroll the whole page. The contract: every page's root
          element must set h="100%" so it stretches into this column. */}
      <Box
        as="main"
        flex="1"
        minH={0}
        overflow="hidden"
        bg="bg"
      >
        <Box
          maxW="1400px"
          mx="auto"
          px={{ base: 4, md: 8, xl: 10 }}
          py={{ base: 5, md: 8 }}
          h="100%"
          display="flex"
          flexDirection="column"
          minH={0}
        >
          <Outlet />
        </Box>
      </Box>
    </Flex>
  );
}
